<template>
  <div class="ls-location-finder">
    <div class="ls-location-finder__list">
      <div v-if="listView">
        <form
          class="input-group input-group--short"
          @submit.prevent="searchLocation"
        >
          <button
            class="btn btn-light border py-2 px-3"
            type="button"
            aria-label="Get location"
            @click="getLocation"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              xmlns:xlink="http://www.w3.org/1999/xlink"
              width="24"
              height="24"
              viewBox="0 0 24 24"
            >
              <g transform="translate(15 -4.094) rotate(45)">
                <path
                  id="Path_929"
                  data-name="Path 929"
                  d="M9.259,16.461l9.259,2.058L9.259,0,0,18.518Zm0,0V8.23"
                  fill="none"
                  stroke="#ee3124"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="1.5"
                />
              </g>
            </svg>
          </button>
          <input
            type="text"
            class="form-control mb-0"
            placeholder="Search City, State or Zip Code"
            v-model="searchInput"
            aria-label="Search locations by address or zip code"
          />
          <button
            type="submit"
            class="btn btn-primary"
          >
            Search
          </button>
        </form>
        <div v-if="locationError" aria-live="polite" style="font-size:14px;color:red">
          Invalid location. Please try again.
        </div>
        <div v-if="userLocationError" aria-live="polite" style="font-size:14px;color:red">
          Unable to get location. Please type in your location.
        </div>
        <tag-filter :options="types"
                    class="mb-4"
                    @option-clicked="typeClickedHandler($event)"
                    filter-title="Locations Filters" />

        <div class="ls-location-finder__list--locations"
             v-if="visibleLocations.length > 0">
          <a class="ls-location border-bottom"
             v-for="(location, index) in visibleLocations"
             :key="`location-${index}`"
             href="javascript:void(0)"
             @click="openDetails(location.Id)">
            <p class="ls-location__number">{{ index + 1 }}</p>
            <p class="h3">{{ location.Title }}</p>
            <address v-if="location.HasAddress">
              {{ location.Street }}<br />
              {{ location.City }}, {{ location.State }} {{ location.Zip }}<br />
              <div v-if="location.Phone.length">
                {{
                formatPhoneNumber(location.Phone)
                }}
              </div>
            </address>
          </a>
        </div>
        <p class="h4" v-else>No locations match the current filter.</p>
      </div>
      <div v-if="!listView"
           class="d-flex flex-column justify-content-start location-detail__overlay">
        <button @click="closeDetails()"
                class="bg-primary border-0 text-white py-2 px-3 w-100 text-start">
          <svg xmlns="http://www.w3.org/2000/svg"
               class="heroicon-svg me-2"
               fill="none"
               viewBox="0 0 24 24"
               stroke="currentColor">
            <path stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M7 16l-4-4m0 0l4-4m-4 4h18" />
          </svg>
          Back to Branch List
        </button>
        <div class="details-img__box">
          <div class="detail-img"
               v-if="activeLocation.ImageUrl"
               :style="`background-image: url('${activeLocation.ImageUrl}')`"></div>
        </div>
        <div class="row mb-5 px-3 px-lg-0">
          <div class="col-7">
            <p class="h3">{{ activeLocation.Title }}</p>
            <address v-if="activeLocation.HasAddress">
              {{ activeLocation.Street }}<br />
              {{ activeLocation.City }}, {{ activeLocation.State }} {{ activeLocation.Zip }}<br />
              <a :href="`tel:${activeLocation.Phone.replace(/[^0-9]/, '')}`"
                 v-if="activeLocation.Phone">
                {{
                formatPhoneNumber(activeLocation.Phone)
                }}
              </a>
            </address>
            <span v-html="activeLocation.Hours"></span>
          </div>
          <div class="col-4 text-end">
            <a :href="`https://maps.google.com?saddr=Current+Location&daddr=${activeLocation.Latitude},${activeLocation.Longitude}`"
               target="_blank"
               class="direction-link text-center d-inline-block mt-4 pt-3">
              <svg xmlns="http://www.w3.org/2000/svg"
                   width="28.284"
                   height="28.284"
                   viewBox="0 0 28.284 28.284">
                <g id="Group_1207"
                   data-name="Group 1207"
                   transform="translate(0 0)">
                  <path id="Rectangle_1054"
                        data-name="Rectangle 1054"
                        d="M2,2V18H18V2H2M1,0H19a1,1,0,0,1,1,1V19a1,1,0,0,1-1,1H1a1,1,0,0,1-1-1V1A1,1,0,0,1,1,0Z"
                        transform="translate(14.143 0) rotate(45)"
                        fill="#0093b2" />
                  <g id="Group_1206"
                     data-name="Group 1206"
                     transform="translate(11.424 9.465)">
                    <path id="Path_781"
                          data-name="Path 781"
                          d="M16013.66,3465.138a1,1,0,0,1-1-1v-5.709a1,1,0,0,1,1-1h7.234a1,1,0,0,1,0,2h-6.234v4.709A1,1,0,0,1,16013.66,3465.138Z"
                          transform="translate(-16013.66 -3455.735)"
                          fill="#0093b2" />
                    <path id="Path_782"
                          data-name="Path 782"
                          d="M16017.145,3462.431a1,1,0,0,1-.707-1.707l2.014-2.014-2.014-2.014a1,1,0,1,1,1.414-1.414l2.721,2.721a1,1,0,0,1,0,1.414l-2.721,2.721A1,1,0,0,1,16017.145,3462.431Z"
                          transform="translate(-16012.194 -3455.989)"
                          fill="#0093b2" />
                  </g>
                </g>
              </svg>
              <br />
              Directions
            </a>
            <div class="types-wrapper mt-5">
              <p v-for="(type, index) in activeLocation.Types"
                 class="detail-types text-nowrap">
                {{type}}
              </p>
            </div>
          </div>
        </div>
        <div class="ps-3 ps-lg-0">
          <a :href="detailsUrl + activeLocation.Url">
            See Branch Details
          </a>
        </div>
      </div>
    </div>
    <div class="ls-location-finder__map">
      <google-map :apiKey="apiKey"
                  :initialMarker="false"
                  :center="{ lat: latitude, lng: longitude }"
                  :markers="markers"
                  :zoom="currentZoom"
                  @setActive="openDetails($event)" />
    </div>
  </div>
  <modal :modalUrl="modalUrl"
         :leaveButtonClasses="additionalClass"
         :dialogClasses="dialogClass"
         @modalMounted="handleMount($event)"
         @stayLinkClicked="closeModal"
         @leaveLinkClicked="closeModal"
         :newWindow="true">
    <template v-slot>
      <div v-html="messageHtml"></div>
    </template>
  </modal>
</template>

<script>
  import { computed, onUpdated, reactive, ref } from 'vue';
  import axios from 'axios';

  import { formatPhoneNumber } from '../helpers/formatting';
  import { orderByObjectNumber } from '../helpers/sorting';

  import TagFilter from './TagFilter.vue';
  import GoogleMap from './GoogleMap.vue';
  import Modal from './Modal.vue';

  export default {
    components: { TagFilter, GoogleMap, Modal },
    name: 'LocationFinder',
    props: {
      apiKey: {
        type: String,
        required: true,
      },
      geocodeApiKey: {
        type: String,
        required: true,
      },
      center: {
        type: Object || null,
        required: true,
      },
      detailsUrl: {
        type: String,
        required: true,
      },
      locations: {
        type: Array || null,
        required: true,
      },
      zoom: {
        type: Number,
        required: false,
        default: 10,
      },
    },
    setup(props) {
      // Search
      const searchInput = ref('');
      const search = ref('');
      const listView = ref(true);
      const locationError = ref(false);
      const userLocationError = ref(false);
      const activeLocation = ref({});
      const loadCount = ref(0);
      let searchTimeout;
      const setSearch = () => {
        search.value = searchInput.value;
      };
      const searchHandler = () => {
        clearTimeout(searchTimeout);
        searchTimeout = setTimeout(setSearch, 500);
      };
      const googleApiUrl = 'https://maps.googleapis.com/maps/api/geocode/json';
      const geocodeApiKey = 'AIzaSyAUjSn4klShVGvlkAfsLgMnBRopbmdsG3g'

      // Filter
      const types = reactive([]);
      const selectedTypes = reactive([]);
      const typeClickedHandler = (eventTypes) => {
        selectedTypes.length = 0;
        eventTypes.forEach((type) => {
          selectedTypes.push({ ...type });
        });
      };
      const typesList = props.locations.flatMap((loc) => loc.Types);
      typesList.forEach((type) => {
        if (!types.map((ogType) => ogType.value).includes(type)) {
          types.push({
            name: type,
            value: type,
          });
        }
      });

      // Locations
      const latitude = ref(props.center.lat);
      const longitude = ref(props.center.lng);
      const currentZoom = ref(props.zoom);
      const markers = computed(() => {
        return visibleLocations.value.map((loc) => ({
          id: loc.Id,
          locationInfo: {
            city: loc.City,
            hours: loc.Hours,
            imageAlt: loc.ImageAlt,
            imageUrl: loc.ImageUrl,
            phone: loc.Phone,
            state: loc.State,
            street: loc.Street,
            title: loc.Title,
            zip: loc.Zip,
          },
          position: {
            lat: loc.Latitude,
            lng: loc.Longitude,
          },
        }));
      });
      const setMapFromUsersLocation = async (location) => {
        if (location.coords) {
          latitude.value = location.coords.latitude;
          longitude.value = location.coords.longitude;
          const response = await getZipFromLatLong(location.coords.latitude, location.coords.longitude);

          if (response.data.status === 'OK') {
            userLocationError.value = false;
            const { long_name: zipCode } = response.data.results[0].address_components[0];

            searchInput.value = zipCode;
            search.value = zipCode;
            searchLocation();
          }
          else {
            userLocationError.value = true;
          }
        }
      };
      const getLocation = () => {
        if (window.navigator && window.navigator.geolocation) {
          window.navigator.geolocation.getCurrentPosition(
            setMapFromUsersLocation,
            (err) => console.error(err)
          );
        }
      };

      const getZipFromLatLong = (lat, lng) => {
        const apiUrl = `${googleApiUrl}?latlng=${lat},${lng}&result_type=postal_code&key=${geocodeApiKey}`;
        return axios.get(apiUrl);
      }

      // Helpers
      const degreesToRadians = (degrees) => {
        return (degrees * Math.PI) / 180;
      };

      const distanceInMiBetweenEarthCoordinates = (lat1Init, lon1) => {
        const earthRadiusMi = 3959;
        const lat2Init = latitude.value;
        const lon2 = longitude.value;

        const dLat = degreesToRadians(lat2Init - lat1Init);
        const dLon = degreesToRadians(lon2 - lon1);

        const lat1 = degreesToRadians(lat1Init);
        const lat2 = degreesToRadians(lat2Init);

        const a =
          Math.sin(dLat / 2) * Math.sin(dLat / 2) +
          Math.sin(dLon / 2) *
          Math.sin(dLon / 2) *
          Math.cos(lat1) *
          Math.cos(lat2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

        return earthRadiusMi * c;
      };

      const searchLocation = async () => {
        const response = await getLatLongFromAddress(searchInput.value);

        if (response.data.status === 'OK') {
          const { lat, lng } = response.data.results[0].geometry.location;
          locationError.value = false;

          latitude.value = lat
          longitude.value = lng
        }
        else {
          locationError.value = true;
        }
      }

      const getLatLongFromAddress = (address) => {
        const apiUrl = `${googleApiUrl}?address=${address}&key=${geocodeApiKey}`;
        return axios.get(apiUrl);
      }

      const visibleLocations = computed(() => {
        if (loadCount.value < 1) {
          loadCount.value += 1;
        }

        let vLocations = props.locations.map((loc) => ({
          ...loc,
          HasAddress: !!(loc.Street.length && loc.City.length && loc.State.length),
          Distance: distanceInMiBetweenEarthCoordinates(
            loc.Latitude,
            loc.Longitude
          ),
        }));

        // Only show locations wihtin 50 miles of searched location
        if (loadCount.value > 1) {
          vLocations = vLocations.filter(location => location.Distance <= 50);
        }

        // Type filter
        if (selectedTypes.length > 0) {
          const typeValues = selectedTypes.map((type) => type.value);

          vLocations = vLocations.filter(
            (loc) =>
              loc.Types && loc.Types.some((type) => typeValues.includes(type))
          );
        }

        return vLocations.sort(orderByObjectNumber('Distance'));
      });

      // Detail View
      const openDetails = (id) => {
        activeLocation.value = visibleLocations.value.find(l => l.Id === id);
        listView.value = false;
      };

      const closeDetails = () => {
        listView.value = true;
        activeLocation.value = {};
      };

      const additionalClass = 'offsite-link';
      const dialogClass = 'modal-dialog-centered'
      const messageHtml = "Please be advised that you are leaving City Bank's website. This link is provided as a courtesy.  City Bank does not endorse or control the content of third party websites.";
      const modalUrl = ref('');
      let modal = undefined;

      const openModal = () => {
        if (modal) {
          modal.show();
        }
      };

      const closeModal = () => {
        if (modal) {
          modal.hide();
        }
      };

      const handleMount = (modalEvent) => {
        modal = modalEvent
      };

      const watchLink = (event) => {
        const self = event.currentTarget;
        if (self) {
          const url = self.getAttribute('href');
          // eslint-disable-next-line no-script-url
          if (
            url &&
            !url.startsWith('#') &&
            !url.includes('javascript:') &&
            !self.classList.contains('offsite-link') &&
            !url.includes('tel:') &&
            !url.includes('mailto:')
          ) {
            if (self.host !== window.location.host) {
              event.preventDefault();

              if (modal) {
                modalUrl.value = url;
                openModal();
              } else {
                window.location.href = url;
              }
            }
          }
        }
      };

      onUpdated(() => {
        const links = document.querySelectorAll('a');
        links.forEach((link) => {
          link.addEventListener('click', watchLink);
        });
      });

      return {
        currentZoom,
        formatPhoneNumber,
        getLocation,
        latitude,
        longitude,
        markers,
        searchHandler,
        searchInput,
        typeClickedHandler,
        types,
        visibleLocations,
        openDetails,
        listView,
        locationError,
        userLocationError,
        activeLocation,
        closeDetails,
        messageHtml,
        modalUrl,
        additionalClass,
        handleMount,
        closeModal,
        dialogClass,
        searchLocation,
      };
    },
  };
</script>
